import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Typography, 
  TextField, 
  InputAdornment,
  Chip,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import api from '../../services/api';

const SHERegister = ({ onAdd, onView, onEdit, successMessage, onClearMessage }) => {
  const [events, setEvents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const roles = user.roles || [];
  const canAdd = roles.includes('admin') || roles.includes('she_manager') || roles.includes('she_officer') || roles.includes('sys_admin');
  const canEdit = roles.includes('admin') || roles.includes('she_manager') || roles.includes('she_officer') || roles.includes('sys_admin');
  const canDelete = roles.includes('admin') || roles.includes('she_manager') || roles.includes('she_officer') || roles.includes('sys_admin');

  useEffect(() => {
    fetchEvents();
    if (successMessage) {
        setSnackbar({ open: true, message: successMessage, severity: 'success' });
        if (onClearMessage) onClearMessage();
    }
  }, [successMessage, onClearMessage]);

  const fetchEvents = async () => {
    try {
      const response = await api.get('/she-events');
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching SHE events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (event) => {
    setEventToDelete(event);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!eventToDelete) return;
    
    setDeleting(true);
    try {
      await api.delete(`/she-events/${eventToDelete.id}`);
      fetchEvents();
      setDeleteDialogOpen(false);
      setEventToDelete(null);
      setSnackbar({ open: true, message: 'SHE record deleted successfully', severity: 'success' });
    } catch (error) {
      if (error.response) {
        alert(`Error deleting record: ${error.response.data.error || 'Unknown error'}`);
      } else {
        console.error('Error deleting SHE event:', error);
        alert('Failed to connect to backend for deletion.');
      }
    } finally {
      setDeleting(false);
    }
  };

  const filteredEvents = events.filter(event => 
    event.action_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.department?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.owner?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
        case 'high': return '#ef4444';
        case 'medium': return '#f59e0b';
        case 'low': return '#10b981';
        default: return '#94a3b8';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" className="gradient-text" sx={{ fontWeight: 'bold' }}>
          SHE Compliance & Activity Tracker
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            placeholder="Search activity..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'var(--text-secondary)' }} />
                </InputAdornment>
              ),
              sx: { borderRadius: '12px', background: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)' }
            }}
          />
          {canAdd && (
            <Button 
                variant="contained" 
                onClick={onAdd}
                sx={{ 
                    borderRadius: '12px', 
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    textTransform: 'none',
                    px: 4,
                    fontWeight: 'bold',
                    boxShadow: '0 4px 14px 0 rgba(16, 185, 129, 0.4)',
                    fontSize: '1rem'
                }}
            >
                Add Activity
            </Button>
          )}
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(10px)', boxShadow: 'none', borderRadius: '20px', border: '1px solid rgba(255,255,255,0.05)', overflow: 'auto' }}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead sx={{ background: 'rgba(255,255,255,0.03)' }}>
            <TableRow>
              <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Action ID</TableCell>
              <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Date</TableCell>
              <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Category</TableCell>
              <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Department</TableCell>
              <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Description</TableCell>
              <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Priority</TableCell>
              <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
                <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 10 }}>
                        <CircularProgress sx={{ color: '#10b981' }} />
                    </TableCell>
                </TableRow>
            ) : filteredEvents.map((event) => (
              <TableRow 
                key={event.id}
                sx={{ 
                  '&:hover': { background: 'rgba(255,255,255,0.02)' },
                  '& td': { borderColor: 'rgba(255,255,255,0.03)' }
                }}
              >
                <TableCell sx={{ fontWeight: 'bold', color: '#10b981' }}>{event.action_id}</TableCell>
                <TableCell sx={{ color: '#f1f5f9' }}>{event.date}</TableCell>
                <TableCell sx={{ color: '#f1f5f9' }}>{event.activity_category}</TableCell>
                <TableCell sx={{ color: '#f1f5f9' }}>{event.department}</TableCell>
                <TableCell sx={{ color: '#94a3b8', maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {event.description}
                </TableCell>
                <TableCell>
                  <Chip 
                    label={event.priority} 
                    size="small"
                    sx={{ 
                      background: `${getPriorityColor(event.priority)}15`,
                      color: getPriorityColor(event.priority),
                      border: `1px solid ${getPriorityColor(event.priority)}40`,
                      fontWeight: 'bold',
                      fontSize: '0.7rem'
                    }} 
                  />
                </TableCell>
                <TableCell>
                  <Chip 
                    label={event.status} 
                    size="small"
                    sx={{ 
                      background: event.status?.toLowerCase() === 'completed' || event.status?.toLowerCase() === 'closed' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                      color: event.status?.toLowerCase() === 'completed' || event.status?.toLowerCase() === 'closed' ? '#10b981' : '#ef4444',
                      border: `1px solid ${event.status?.toLowerCase() === 'completed' || event.status?.toLowerCase() === 'closed' ? '#10b98140' : '#ef444440'}`,
                      fontWeight: 'bold',
                      fontSize: '0.7rem'
                    }} 
                  />
                </TableCell>
                <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                        <Button 
                            variant="outlined"
                            onClick={() => onView(event)} 
                            sx={{ 
                                color: '#10b981', 
                                border: '2px solid rgba(16, 185, 129, 0.5)',
                                textTransform: 'none', 
                                fontWeight: 'bold',
                                borderRadius: '10px',
                                minWidth: '70px',
                                '&:hover': { 
                                    background: 'rgba(16, 185, 129, 0.1)',
                                    border: '2px solid #10b981'
                                } 
                            }}
                        >
                            View
                        </Button>
                        {canEdit && (
                            <Button 
                                variant="outlined"
                                onClick={() => onEdit(event)} 
                                sx={{ 
                                    color: '#3b82f6', 
                                    border: '2px solid rgba(59, 130, 246, 0.5)',
                                    textTransform: 'none', 
                                    fontWeight: 'bold',
                                    borderRadius: '10px',
                                    minWidth: '70px',
                                    '&:hover': { 
                                        background: 'rgba(59, 130, 246, 0.1)',
                                        border: '2px solid #3b82f6'
                                    } 
                                }}
                            >
                                Edit
                            </Button>
                        )}
                        {canDelete && (
                            <Button 
                                variant="outlined"
                                onClick={() => handleDeleteClick(event)} 
                                sx={{ 
                                    color: '#ef4444', 
                                    border: '2px solid rgba(239, 68, 68, 0.5)',
                                    textTransform: 'none', 
                                    fontWeight: 'bold',
                                    borderRadius: '10px',
                                    minWidth: '70px',
                                    '&:hover': { 
                                        background: 'rgba(239, 68, 68, 0.1)',
                                        border: '2px solid #ef4444'
                                    } 
                                }}
                            >
                                Delete
                            </Button>
                        )}
                    </Box>
                </TableCell>
              </TableRow>
            ))}
            {!loading && filteredEvents.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 10 }}>
                  <Typography variant="h6" sx={{ color: '#475569' }}>
                    No SHE records found matching your criteria.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            background: '#1e293b',
            color: 'white',
            borderRadius: '16px',
            border: '1px solid rgba(255,255,255,0.1)',
            padding: '16px'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: '#ef4444', fontSize: '1.5rem' }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#94a3b8', mt: 1 }}>
            Are you sure you want to delete this SHE record? This action cannot be undone.
            {eventToDelete && (
              <Box sx={{ mt: 2, p: 2, background: 'rgba(255,255,255,0.05)', borderRadius: '8px' }}>
                <Typography variant="body2" sx={{ color: '#10b981', fontWeight: 'bold' }}>
                  {eventToDelete.action_id}
                </Typography>
                <Typography variant="body2" sx={{ color: '#f1f5f9' }}>
                  {eventToDelete.description}
                </Typography>
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ gap: 2, px: 3, pb: 2 }}>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            disabled={deleting}
            sx={{ color: '#94a3b8', textTransform: 'none', fontWeight: 'bold' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            disabled={deleting}
            variant="contained"
            sx={{ 
                background: '#ef4444', 
                '&:hover': { background: '#dc2626' },
                textTransform: 'none',
                fontWeight: 'bold',
                borderRadius: '8px',
                px: 3
            }}
          >
            {deleting ? 'Deleting...' : 'Delete Record'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={(event, reason) => {
          if (reason === 'clickaway') return;
          setSnackbar({ ...snackbar, open: false });
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
            severity={snackbar.severity} 
            variant="filled"
            sx={{ width: '100%', borderRadius: '12px', fontWeight: 'bold', boxShadow: '0 4px 12px rgba(0,0,0,0.5)' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SHERegister;
